import { useState } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  rosterNames: string[]
  /** Roster names that cannot be chosen, with the reason shown beside them. */
  disabledReasons?: Record<string, string>
  onConfirm: (selectedName: string) => void
  onCancel: () => void
}

export default function RosterSelectionModal({ rosterNames, disabledReasons = {}, onConfirm, onCancel }: Props) {
  const [selected, setSelected] = useState<string | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const discardCount = rosterNames.length - 1
  const canConfirm = selected !== null && acknowledged
  const noneSelectable = rosterNames.every((name) => Boolean(disabledReasons[name]))

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="roster-selection-title"
        className="bg-white rounded-2xl shadow-2xl w-full mx-4 flex flex-col"
        style={{ maxWidth: 540, maxHeight: '90vh' }}
      >
        <div className="px-6 pt-5 pb-3 border-b border-gray-100">
          <h2 id="roster-selection-title" className="text-base font-bold text-gray-900">Choose one roster file</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {rosterNames.length} roster files were added, but only one roster can be analysed. Files are never merged.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <p className="text-xs text-gray-600 leading-relaxed">
            If you have scoped rosters (for example HR, Finance and Tech), combine them into one workbook with one sheet per scope, then upload that workbook.
          </p>

          <fieldset className="space-y-1.5">
            <legend className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-2">Roster to analyse</legend>
            {rosterNames.map((name) => {
              const reason = disabledReasons[name]
              return (
                <label
                  key={name}
                  className={`flex items-center gap-2.5 px-3 py-2 border rounded-lg text-sm ${
                    reason ? 'opacity-60 cursor-not-allowed border-gray-200 bg-gray-50' :
                    selected === name ? 'cursor-pointer border-purple-400 bg-purple-50' : 'cursor-pointer border-gray-200 bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="roster-choice"
                    value={name}
                    checked={selected === name}
                    disabled={Boolean(reason)}
                    onChange={() => setSelected(name)}
                  />
                  <span className="text-gray-700 truncate">{name}</span>
                  {reason && <span className="text-[11px] text-gray-500 ml-auto">{reason}</span>}
                </label>
              )
            })}
          </fieldset>

          {noneSelectable && (
            <p role="alert" className="text-xs text-red-600 leading-relaxed">
              None of these files can be used with documents. Remove the JSON files or the documents, then upload again.
            </p>
          )}

          <label className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>
              I understand the other {discardCount} roster file{discardCount === 1 ? '' : 's'} will be discarded and will not be analysed.
            </span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-row gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => { if (selected) onConfirm(selected) }}
            className="flex-1 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            Use selected roster
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
